import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, User as UserIcon, Building2, Shield,
    Edit2, Trash2, ShieldAlert, ShieldCheck, Users, UserPlus, Check, ArrowRight, AlertTriangle, X, DollarSign, Fuel, Clock, Briefcase, Link as LinkIcon, FileDown, Loader2, CalendarDays, Receipt, Utensils
} from 'lucide-react';
import { UserService, SedeService, FigureProfessionaliService, RelazioneGiornalieraService, ConsentService, legalDocTypeLabel } from '../services/apiService';
import { User, Sede, UserRole, FiguraProfessionale } from '../types';
import { useToast } from "../components/ToastContext";
import NotificheAreaToggles, { NotificheArea } from '../components/NotificheAreaToggles';
import { useAuth } from "../contexts/AuthContext";
import { generaRiepilogoAdminCollaboratore } from "../services/generaRiepilogoCollaboratore";
import { generaAgendaCollaboratore } from "../services/generaAgendaCollaboratore";
import { getIncaricoStatoBadge, getTurnoStatoBadge } from "../utils/incaricoUtils";
import NumberInput from '../components/NumberInput';
import ResponsiveTable from '../components/ResponsiveTable';
import AgendaSettimanaleModal from '../components/AgendaSettimanaleModal';
import { ModificaReportModal } from '../components/ModificaReportModal';
import { localIsoDate } from '../utils/utils';
import {
    buildRiepilogoRuoli, formatRiepilogoRuoli, isCambioRuoloOSediGestite,
    normalizeSediGestite, validateSediGestite,
} from '../utils/sediGestite';

export const UtentiView: React.FC = () => {
    const { addToast } = useToast();
    const { user: currentUser, currentSedeId } = useAuth();
    const navigate = useNavigate();
    const isResponsabile = currentUser?.role === UserRole.RESPONSABILE;
    const isAdmin = currentUser?.role === UserRole.ADMIN;

    // Dati
    const [users, setUsers] = useState<User[]>([]);
    const [sedis, setSedis] = useState<Sede[]>([]);
    const [figure, setFigure] = useState<FiguraProfessionale[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modale agenda settimanale
    const [isAgendaSettimanaleOpen, setIsAgendaSettimanaleOpen] = useState(false);

    // Wizard di creazione
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createStep, setCreateStep] = useState(1);
    const [newUserRole, setNewUserRole] = useState<UserRole>(UserRole.COLLABORATORE);
    // Un utente può essere associato a più sedi, tranne il Titolare che è globale.
    const [newUserSedeIds, setNewUserSedeIds] = useState<number[]>([]);
    // Sedi gestite dal nuovo responsabile, tra quelle spuntate.
    const [newUserSediGestite, setNewUserSediGestite] = useState<number[]>([]);
    const [newUserData, setNewUserData] = useState({ name: '', surname: '', email: '' });

    // Costi operatore
    const [newUserCosti, setNewUserCosti] = useState({ costoOrario: 0, costoOrarioSicurezza: 0, rimborsoKm: 0, sogliaBuonoPastoIncarichiOre: 0, importoBuonoPastoIncarichi: 0, sogliaBuonoPastoTurniOre: 0, importoBuonoPastoTurni: 0 });
    const [inviteLink, setInviteLink] = useState<string | null>(null);

    // Riepilogo mensile
    const [riepilogoUser, setRiepilogoUser] = useState<User | null>(null);
    const [riepilogoMonth, setRiepilogoMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [riepilogoLoading, setRiepilogoLoading] = useState(false);

    // Agenda
    const [agendaUser, setAgendaUser] = useState<User | null>(null);
    const [agendaData, setAgendaData] = useState<any | null>(null);
    const [agendaLoading, setAgendaLoading] = useState(false);
    const [agendaFrom, setAgendaFrom] = useState(() => {
        const d = new Date(); d.setDate(1);
        return localIsoDate(d);
    });
    const [agendaTo, setAgendaTo] = useState(() => localIsoDate());

    const handleOpenAgenda = (user: User) => { setAgendaUser(user); setAgendaData(null); };
    const handleLoadAgenda = async () => {
        if (!agendaUser) return;
        setAgendaLoading(true);
        try {
            const data = await UserService.getAgenda(agendaUser.id, agendaFrom, agendaTo);
            setAgendaData(data);
        } catch { addToast('Errore nel caricamento dei dati', 'error'); }
        finally { setAgendaLoading(false); }
    };
    const handleDownloadAgenda = () => { if (agendaData) generaAgendaCollaboratore(agendaData, agendaFrom, agendaTo); };
    // Report aperto in modifica dall'agenda.
    const [modificaReportId, setModificaReportId] = useState<number | null>(null);

    const handleDownloadRiepilogo = async () => {
        if (!riepilogoUser) return;
        setRiepilogoLoading(true);
        try {
            const res = await RelazioneGiornalieraService.getAllApprovedByMonth(riepilogoMonth);
            const all: any[] = Array.isArray(res) ? res : res?.data || [];
            const filtered = all.filter((r: any) =>
                r.collaboratore?.id === riepilogoUser.id ||
                r.collaboratoreId === riepilogoUser.id
            );
            generaRiepilogoAdminCollaboratore(
                filtered,
                riepilogoMonth,
                `${riepilogoUser.name} ${riepilogoUser.surname}`.trim()
            );
            setRiepilogoUser(null);
        } catch {
            addToast('Errore nel caricamento dei dati', 'error');
        } finally {
            setRiepilogoLoading(false);
        }
    };

    // Modale modifica
    const [editingUser, setEditingUser] = useState<User | null>(null);
    // Consensi legali dell'utente aperto in modifica (solo ADMIN, come in CollaboratoriView).
    const [modalConsents, setModalConsents] = useState<{ pending: any[]; accepted: any[] } | null>(null);
    const [editRole, setEditRole] = useState<UserRole>(UserRole.COLLABORATORE);
    // Avvisi da supervisore per area: contano per titolare e responsabile, si conservano al cambio ruolo.
    const [editNotifiche, setEditNotifiche] = useState<NotificheArea>({ notificheFiduciari: true, notificheInvestigativi: true });
    const [editSedeIds, setEditSedeIds] = useState<number[]>([]);
    const [editSediGestite, setEditSediGestite] = useState<number[]>([]);
    // Conferma prima di salvare un cambio di ruolo o di sedi gestite.
    const [confermaRuoli, setConfermaRuoli] = useState<{ riepilogo: string; onConfirm: () => void } | null>(null);
    const [editCosti, setEditCosti] = useState({ costoOrario: 0, costoOrarioSicurezza: 0, rimborsoKm: 0, sogliaBuonoPastoIncarichiOre: 0, importoBuonoPastoIncarichi: 0, sogliaBuonoPastoTurniOre: 0, importoBuonoPastoTurni: 0 });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersRes, sediRes, figureRes] = await Promise.all([
                UserService.getAll(),
                SedeService.getAll(),
                FigureProfessionaliService.getAll().catch(() => ({ success: true, data: [] }))
            ]);
            if(usersRes.success) setUsers(usersRes.data);
            if(sediRes.success) setSedis(sediRes.data);
            if(figureRes && figureRes.success) setFigure(figureRes.data || figureRes);
        } catch (error) {
            addToast("Errore caricamento dati", "error");
        } finally {
            setLoading(false);
        }
    };

    // Il responsabile crea collaboratori nella sede gestita selezionata.
    const sedeCreazioneResponsabile: number | undefined = typeof currentSedeId === 'number'
        ? currentSedeId
        : (currentUser?.sedeId ?? undefined);

    const resetCreateModal = () => {
        setIsCreateModalOpen(false);
        // Il responsabile salta ruolo e sede: crea sempre un COLLABORATORE nella propria sede.
        setCreateStep(isResponsabile ? 3 : 1);
        setNewUserRole(UserRole.COLLABORATORE);
        setNewUserSedeIds(isResponsabile && sedeCreazioneResponsabile ? [sedeCreazioneResponsabile] : []);
        setNewUserSediGestite([]);
        setNewUserData({ name: '', surname: '', email: '' });
        setNewUserCosti({ costoOrario: 0, costoOrarioSicurezza: 0, rimborsoKm: 0, sogliaBuonoPastoIncarichiOre: 0, importoBuonoPastoIncarichi: 0, sogliaBuonoPastoTurniOre: 0, importoBuonoPastoTurni: 0 });
        setInviteLink(null);
    };

    const openCreateModal = () => {
        if (isResponsabile) {
            setCreateStep(3);
            setNewUserRole(UserRole.COLLABORATORE);
            setNewUserSedeIds(sedeCreazioneResponsabile ? [sedeCreazioneResponsabile] : []);
        }
        setIsCreateModalOpen(true);
    };

    // Step 1, ruolo. Il Titolare è globale e va direttamente ai dati, gli altri
    // passano dalla scelta sedi. Si decide dal ruolo passato, non dallo state (async).
    const handleSelectRole = (role: UserRole) => {
        setNewUserRole(role);
        // Per un nuovo responsabile le sedi già spuntate partono come gestite.
        setNewUserSediGestite(role === UserRole.RESPONSABILE ? newUserSedeIds : []);
        if (role === UserRole.ADMIN) {
            setNewUserSedeIds([]);
            setCreateStep(3);
        } else {
            setCreateStep(2);
        }
    };

    // Una sede spuntata per un nuovo responsabile parte come gestita; togliendo
    // la spunta esce anche dalle sedi gestite.
    const toggleNewUserSede = (id: number) => {
        const checked = newUserSedeIds.includes(id);
        setNewUserSedeIds(prev => checked ? prev.filter(x => x !== id) : [...prev, id]);
        setNewUserSediGestite(prev => checked ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // In modifica una sede aggiunta parte come gestita solo se l'utente non ne gestisce altre.
    const toggleEditSede = (id: number) => {
        const checked = editSedeIds.includes(id);
        setEditSedeIds(prev => checked ? prev.filter(x => x !== id) : [...prev, id]);
        setEditSediGestite(prev => {
            if (checked) return prev.filter(x => x !== id);
            const gestiteAttuali = prev.filter(x => editSedeIds.includes(x));
            return gestiteAttuali.length === 0 ? [...prev, id] : prev;
        });
    };

    const setRuoloSede = (setter: React.Dispatch<React.SetStateAction<number[]>>) =>
        (id: number, responsabile: boolean) =>
            setter(prev => responsabile
                ? (prev.includes(id) ? prev : [...prev, id])
                : prev.filter(x => x !== id));

    // Passando a Responsabile senza sedi gestite le gestisce tutte, come fa il server.
    const handleEditRoleChange = (role: UserRole) => {
        setEditRole(role);
        if (role === UserRole.RESPONSABILE
            && editSediGestite.filter(id => editSedeIds.includes(id)).length === 0) {
            setEditSediGestite(editSedeIds);
        }
    };

    const nomeSede = (id: number) => sedis.find(s => s.id === id)?.name || `Sede #${id}`;

    const handleNextStep = () => {
        if (createStep === 2) {
            // Il Responsabile deve avere almeno una sede; il Collaboratore può essere associato dopo.
            if (newUserRole === UserRole.RESPONSABILE && newUserSedeIds.length === 0) {
                addToast("Seleziona almeno una sede per il Responsabile", "info"); return;
            }
            const err = validateSediGestite(newUserRole, newUserSedeIds, newUserSediGestite);
            if (err) { addToast(err, "info"); return; }
            setCreateStep(3);
        } else if (createStep === 3) {
            if (!newUserData.name || !newUserData.surname || !newUserData.email) { addToast("Compila tutti i campi", "info"); return; }
            setCreateStep(4);
        }
    };

    const handleCreateUser = () => {
        if (isAdmin && newUserRole === UserRole.RESPONSABILE) {
            const err = validateSediGestite(newUserRole, newUserSedeIds, newUserSediGestite);
            if (err) { addToast(err, "error"); return; }
            setConfermaRuoli({
                riepilogo: formatRiepilogoRuoli(
                    buildRiepilogoRuoli(newUserRole, newUserSedeIds, newUserSediGestite, nomeSede)),
                onConfirm: doCreateUser,
            });
            return;
        }
        doCreateUser();
    };

    const doCreateUser = async () => {
        try {
            const payload: any = {
                ...newUserData,
                role: newUserRole,
                // Il Titolare resta globale; gli altri ruoli portano le sedi scelte.
                sedeIds: newUserRole === UserRole.ADMIN ? [] : newUserSedeIds,
                ...newUserCosti
            };
            // Solo il titolare sceglie il ruolo per sede.
            if (isAdmin) {
                payload.sediGestiteIds = normalizeSediGestite(newUserRole, newUserSedeIds, newUserSediGestite);
            }

            const res = await UserService.create(payload);
            const link = typeof res === 'string' ? res : (res as any).data || (res as any).message;

            if (link && (link.startsWith('http') || link.includes('/invite/'))) setInviteLink(link);
            else setInviteLink("Link inviato via email (Simulazione)");

            loadData();
            addToast("Utente creato con successo", "success");
        } catch (error: any) {
            const msg = error.response?.data?.message || "Errore creazione utente";
            addToast(msg, "error");
        }
    };

    const handleEditClick = (user: User) => {
        setEditingUser(user);
        setEditRole(user.role);
        setEditNotifiche({
            notificheFiduciari: (user as any).notificheFiduciari !== false,
            notificheInvestigativi: (user as any).notificheInvestigativi !== false,
        });
        const sedeIdsUtente = user.sedeIds && user.sedeIds.length > 0
            ? user.sedeIds
            : (user.sedeId ? [user.sedeId] : []);
        setEditSedeIds(sedeIdsUtente);
        setEditSediGestite(user.role === UserRole.RESPONSABILE
            ? (user.sediGestiteIds ?? sedeIdsUtente)
            : []);
        setEditCosti({
            costoOrario: (user as any).costoOrario || 0,
            costoOrarioSicurezza: (user as any).costoOrarioSicurezza || 0,
            rimborsoKm: (user as any).rimborsoKm || 0,
            sogliaBuonoPastoIncarichiOre: (user as any).sogliaBuonoPastoIncarichiOre || 0,
            importoBuonoPastoIncarichi: Number((user as any).importoBuonoPastoIncarichi) || 0,
            sogliaBuonoPastoTurniOre: (user as any).sogliaBuonoPastoTurniOre || 0,
            importoBuonoPastoTurni: Number((user as any).importoBuonoPastoTurni) || 0,
        });
        // Consensi legali dell'utente (endpoint ADMIN-only).
        setModalConsents(null);
        if (isAdmin && user.id != null) {
            ConsentService.getUserConsents(user.id)
                .then(s => setModalConsents({ pending: s.pending || [], accepted: s.accepted || [] }))
                .catch(() => { /* Non ADMIN o 403: sezione non mostrata */ });
        }
    };

    const handleSaveEdit = () => {
        if (!editingUser) return;
        if (isAdmin) {
            const err = validateSediGestite(editRole, editSedeIds, editSediGestite);
            if (err) { addToast(err, "error"); return; }
            const gestiteDopo = normalizeSediGestite(editRole, editSedeIds, editSediGestite);
            const cambio = isCambioRuoloOSediGestite(
                { role: editingUser.role, sediGestiteIds: editingUser.sediGestiteIds ?? [] },
                { role: editRole, sediGestiteIds: gestiteDopo },
            );
            if (cambio) {
                const riepilogo = editRole === UserRole.RESPONSABILE
                    ? formatRiepilogoRuoli(buildRiepilogoRuoli(editRole, editSedeIds, editSediGestite, nomeSede))
                    : editRole === UserRole.ADMIN
                        ? 'Diventerà Titolare con accesso globale: non gestirà sedi specifiche.'
                        : `Sarà collaboratore di: ${editSedeIds.length > 0 ? editSedeIds.map(nomeSede).join(', ') : 'nessuna sede'} · non gestirà alcuna sede.`;
                setConfermaRuoli({ riepilogo, onConfirm: doSaveEdit });
                return;
            }
        }
        doSaveEdit();
    };

    const doSaveEdit = async () => {
        if (!editingUser) return;
        try {
            const payload: any = {
                ...editingUser,
                role: editRole,
                // Il Titolare non ha sede; gli altri ruoli portano le sedi scelte.
                sedeIds: editRole === UserRole.ADMIN ? [] : editSedeIds,
                // Azzeriamo il vecchio sedeId, altrimenti con sedeIds=[] il backend
                // ripiegherebbe su quello e lo svuotamento verrebbe ignorato.
                sedeId: null,
                ...editCosti,
                ...editNotifiche,
            };
            // Le sedi gestite le imposta solo il titolare: il responsabile non le invia.
            if (isAdmin) {
                payload.sediGestiteIds = normalizeSediGestite(editRole, editSedeIds, editSediGestite);
            } else {
                delete payload.sediGestiteIds;
            }
            await UserService.update(editingUser.id, payload);
            addToast("Utente aggiornato", "success");
            loadData();
            setEditingUser(null);
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Errore aggiornamento", "error");
        }
    };

    const handleDelete = async (id: number) => {
        if(window.confirm("Sei sicuro di voler eliminare questo utente? L'operazione è irreversibile: il nome viene ridotto alle iniziali e l'accesso viene bloccato.")) {
            try {
                await UserService.delete(id);
                addToast("Utente eliminato", "success");
                loadData();
            } catch (e: any) {
                addToast(e?.response?.data?.message || "Errore: l'utente non è stato eliminato", "error");
            }
        }
    }

    // Copia il link d'invito di un utente "In attesa"
    const handleCopyInvite = async (userId: number) => {
        try {
            const res = await UserService.getInviteLink(userId);
            if (res.success && res.data) {
                await navigator.clipboard.writeText(res.data);
                addToast("Link di invito copiato negli appunti!", "success");
            } else {
                addToast("Impossibile recuperare il link (L'utente è già attivo?)", "error");
            }
        } catch (e) {
            addToast("Errore durante il recupero del link", "error");
        }
    };

    const renderSelettoreFigura = (isEdit: boolean) => (
        <div className="mb-6 bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
            <label className="block text-xs font-bold text-indigo-800 uppercase mb-2 flex items-center gap-2">
                <Briefcase size={14}/> Auto-compila da Figura Professionale
            </label>
            <select
                className="w-full p-2.5 border border-indigo-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-gray-700 shadow-sm"
                onChange={e => {
                    const f = figure.find(fig => fig.id === Number(e.target.value));
                    if(f) {
                        if(isEdit) setEditCosti({...editCosti, costoOrario: f.tariffa});
                        else setNewUserCosti({...newUserCosti, costoOrario: f.tariffa});
                        addToast(`Tariffa aggiornata a € ${f.tariffa}/h`, "info");
                    }
                }}
            >
                <option value="">-- Seleziona una qualifica per caricare la tariffa base --</option>
                {figure.map(f => (
                    <option key={f.id} value={f.id}>{f.nome} (€ {f.tariffa.toFixed(2)} /h)</option>
                ))}
            </select>
        </div>
    );

    const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));

    // In UI "Titolare" al posto di "Admin"; l'enum interno non cambia (vedi formatRole).
    const getRoleBadge = (role: string) => {
        switch (role) {
            case 'ADMIN': return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-200">Titolare</span>;
            case 'RESPONSABILE': return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full border border-purple-200">Responsabile</span>;
            default: return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full border border-blue-200">Collaboratore</span>;
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestione Utenti</h1>
                    <p className="text-sm text-gray-500">Gestisci ruoli, permessi, sedi e costi orari/chilometrici del personale.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setIsAgendaSettimanaleOpen(true)}
                        className="flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg shadow-sm transition-colors"
                        title="Calendario settimanale impegni collaboratori"
                    >
                        <CalendarDays size={18}/> Agenda Settimanale
                    </button>
                    <button
                        onClick={() => navigate('/amministrazione/report-mensile')}
                        className="flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg shadow-sm transition-colors"
                        title="Apri Ore &amp; Spese Collaboratori"
                    >
                        <Receipt size={18}/> Ore &amp; Spese Collaboratori
                    </button>
                    <button onClick={openCreateModal} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm">
                        <UserPlus size={18}/> Nuovo Utente
                    </button>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <Search className="text-gray-400" size={20}/>
                <input type="text" placeholder="Cerca utente..." className="flex-1 outline-none text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400">Caricamento utenti...</div>
                ) : (
                <ResponsiveTable
                    data={filteredUsers}
                    keyExtractor={(u) => u.id}
                    emptyMessage="Nessun utente trovato."
                    columns={[
                        {
                            key: 'utente',
                            label: 'Utente',
                            render: (user) => (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center font-bold text-gray-600 border border-gray-200 shadow-sm">{user.name.charAt(0)}</div>
                                    <div><div className="font-bold text-gray-900">{user.name} {user.surname}</div><div className="text-xs text-gray-500">{user.email}</div></div>
                                </div>
                            ),
                        },
                        {
                            key: 'stato',
                            label: 'Stato',
                            render: (user) => (
                                user.confirmed ? (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full border border-green-200 inline-flex items-center gap-1 w-max">
                                        <Check size={12}/> Attivo
                                    </span>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full border border-amber-200 inline-flex items-center gap-1 w-max">
                                            <Clock size={12}/> In attesa
                                        </span>
                                        <button
                                            onClick={() => handleCopyInvite(user.id)}
                                            className="p-1.5 bg-white text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-md transition-colors shadow-sm"
                                            title="Copia o rinnova Link d'Invito"
                                        >
                                            <LinkIcon size={14}/>
                                        </button>
                                    </div>
                                )
                            ),
                        },
                        {
                            key: 'ruolo',
                            label: 'Ruolo',
                            render: (user) => getRoleBadge(user.role),
                        },
                        {
                            key: 'sede',
                            label: 'Sedi',
                            render: (user) => {
                                const ids = user.sedeIds && user.sedeIds.length > 0
                                    ? user.sedeIds
                                    : (user.sedeId ? [user.sedeId] : []);
                                const gestite = user.role === UserRole.RESPONSABILE ? (user.sediGestiteIds ?? []) : [];
                                const voci = ids
                                    .map(id => ({ id, name: sedis.find(s => s.id === id)?.name, gestita: gestite.includes(id) }))
                                    .filter(v => !!v.name);
                                return (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <Building2 size={16} className="text-gray-400 flex-shrink-0"/>
                                        {voci.length > 0
                                            ? (
                                                <span className="text-sm flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                                    {voci.map((v, i) => (
                                                        <span key={v.id} className="inline-flex items-center gap-1">
                                                            {v.name}
                                                            {v.gestita && (
                                                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded border border-purple-200" title="Responsabile di questa sede">Resp.</span>
                                                            )}
                                                            {i < voci.length - 1 && <span className="text-gray-400">,</span>}
                                                        </span>
                                                    ))}
                                                </span>
                                            )
                                            : <span className="text-gray-400 italic">{user.role === UserRole.ADMIN ? 'Globale' : 'Nessuna sede'}</span>}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'costi',
                            label: 'Costi Riconosciuti',
                            hideOn: 'mobile',
                            render: (user) => {
                                const costoH = (user as any).costoOrario || 0;
                                const costoSicurezza = (user as any).costoOrarioSicurezza || 0;
                                const rimbKm = (user as any).rimborsoKm || 0;
                                const hasCosti = costoH > 0 || costoSicurezza > 0 || rimbKm > 0;
                                return hasCosti ? (
                                    <div className="flex flex-col gap-1 text-[11px] font-medium text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 w-max">
                                        {costoH > 0 && <span className="flex items-center gap-1"><Clock size={12} className="text-blue-500"/> {costoH.toFixed(2)} €/h (Inv)</span>}
                                        {costoSicurezza > 0 && <span className="flex items-center gap-1"><Shield size={12} className="text-purple-500"/> {costoSicurezza.toFixed(2)} €/h (Sic)</span>}
                                        {rimbKm > 0 && <span className="flex items-center gap-1"><Fuel size={12} className="text-green-500"/> {rimbKm.toFixed(2)} €/km</span>}
                                    </div>
                                ) : (
                                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100 font-medium inline-flex items-center gap-1 w-max">
                                        <AlertTriangle size={12}/> Tariffe non impostate
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'actions',
                            label: 'Azioni',
                            align: 'right',
                            render: (user) => (
                                <div className="flex justify-end gap-2">
                                    {/* Il responsabile vede l'agenda solo dei collaboratori della sua sede, non dei Titolari. */}
                                    {(isAdmin || (isResponsabile && user.role === UserRole.COLLABORATORE)) && (
                                        <button onClick={() => handleOpenAgenda(user)} className="p-2 text-blue-600 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-lg shadow-sm" title="Agenda attività"><CalendarDays size={16}/></button>
                                    )}
                                    {/* Il responsabile non può modificare o eliminare i Titolari. */}
                                    {(isAdmin || (isResponsabile && user.role === UserRole.COLLABORATORE)) && (
                                        <>
                                            <button onClick={() => handleEditClick(user)} className="p-2 text-blue-600 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-lg shadow-sm" title="Modifica Utente"><Edit2 size={16}/></button>
                                            <button onClick={() => handleDelete(user.id)} className="p-2 text-red-500 bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50 rounded-lg shadow-sm" title="Elimina Utente"><Trash2 size={16}/></button>
                                        </>
                                    )}
                                </div>
                            ),
                        },
                    ]}
                />
                )}
            </div>

            {/* Modale agenda collaboratore */}
            {agendaUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Agenda Collaboratore</h2>
                                <p className="text-xs text-gray-500 mt-0.5">{agendaUser.name} {agendaUser.surname}</p>
                            </div>
                            <button onClick={() => setAgendaUser(null)} className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full shadow-sm"><X size={18}/></button>
                        </div>
                        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
                            <div className="flex flex-wrap gap-3 items-end">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Dal</label>
                                    <input type="date" value={agendaFrom} onChange={e => { setAgendaFrom(e.target.value); setAgendaData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Al</label>
                                    <input type="date" value={agendaTo} onChange={e => { setAgendaTo(e.target.value); setAgendaData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"/>
                                </div>
                                <button onClick={handleLoadAgenda} disabled={agendaLoading}
                                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2 transition-colors">
                                    {agendaLoading ? <Loader2 size={14} className="animate-spin"/> : <CalendarDays size={14}/>} Carica
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                            {!agendaData && !agendaLoading && (
                                <div className="text-center py-12 text-gray-400">
                                    <CalendarDays size={40} className="mx-auto mb-3 opacity-30"/>
                                    <p className="text-sm">Seleziona un intervallo e premi Carica</p>
                                </div>
                            )}
                            {agendaData && (() => {
                                const incarichi: any[] = agendaData.incarichi || [];
                                const turni: any[] = agendaData.turni || [];
                                // I 5 KPI mostrano solo valori già riconosciuti dal titolare/responsabile,
                                // cioè quanto verrà effettivamente liquidato.
                                // - Incarichi: esclusi RIFIUTATO/REVOCATO.
                                // - Relazioni: solo APPROVATO.
                                // - Ore indagini: solo relazioni APPROVATO; oreApprovateTotali se presente,
                                // altrimenti oreGps + oreExtraApprovate.
                                // - Turni: esclusi REVOCATI e RIFIUTATI.
                                // - Ore turni: oreApprovate (con override prioritario).
                                const incarichiValidi = incarichi.filter((i: any) =>
                                    i.stato !== 'RIFIUTATO' && i.stato !== 'REVOCATO');
                                const turniValidi = turni.filter((t: any) =>
                                    !t.dataRevoca && t.statoAccettazione !== 'RIFIUTATO');
                                const relazioniApprovate = incarichi.reduce(
                                    (acc: number, i: any) => acc + (i.relazioni || []).filter(
                                        (r: any) => r.stato === 'APPROVATO').length, 0);
                                // Ore "Xh Ym" arrotondate a 10 minuti.
                                const fmtHM = (h: number): string => {
                                    if (!h || h <= 0) return '0m';
                                    const totalMin = Math.round((h * 60) / 10) * 10;
                                    const hh = Math.floor(totalMin / 60);
                                    const mm = totalMin % 60;
                                    if (hh === 0) return `${mm}m`;
                                    if (mm === 0) return `${hh}h`;
                                    return `${hh}h ${mm}m`;
                                };
                                const totOreIndagini = incarichi.reduce((acc: number, inc: any) => {
                                    const approvate = (inc.relazioni || []).filter(
                                        (r: any) => r.stato === 'APPROVATO');
                                    return acc + approvate.reduce((a: number, r: any) => {
                                        if (r.oreApprovateTotali != null) {
                                            return a + Math.max(0, Number(r.oreApprovateTotali));
                                        }
                                        const extra = r.oreExtraApprovate != null
                                            ? Number(r.oreExtraApprovate)
                                            : Number(r.oreExtra || 0);
                                        return a + (r.oreGps || 0) + Math.max(0, extra);
                                    }, 0);
                                }, 0);
                                // Stessa logica di generaAgendaCollaboratore.oreTurno: solo turni con ore
                                // approvate; override se presente, altrimenti GPS + ore extra del report.
                                // Pianificati, in corso o da approvare valgono 0.
                                const totOreTurni = turni.reduce((acc: number, t: any) => {
                                    if (!t.oreApprovate) return acc;
                                    if (t.oreCollaboratoreOverride != null) {
                                        return acc + Math.max(0, Number(t.oreCollaboratoreOverride));
                                    }
                                    let ore = 0;
                                    if (t.effettivoInizio && t.effettivoFine) {
                                        ore += Math.max(0, (new Date(t.effettivoFine).getTime() - new Date(t.effettivoInizio).getTime()) / 3_600_000);
                                    }
                                    ore += Math.max(0, Number(t.report?.oreExtra || 0));
                                    return acc + ore;
                                }, 0);
                                // Km approvati: relazioni APPROVATO + report dei turni con ore approvate.
                                // Fallback su kmPercorsi per le relazioni senza kmApprovati.
                                const totKmApprovati = incarichi.reduce((acc: number, inc: any) => {
                                    const approvate = (inc.relazioni || []).filter((r: any) => r.stato === 'APPROVATO');
                                    return acc + approvate.reduce((a: number, r: any) =>
                                        a + Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? 0)), 0);
                                }, 0) + turni.reduce((acc: number, t: any) => {
                                    if (!t.oreApprovate) return acc;
                                    const km = t.report?.kmApprovati ?? t.report?.kmPercorsi ?? 0;
                                    return acc + Math.max(0, Number(km));
                                }, 0);
                                // Il colore del KPI va solo sulla barra laterale e sul valore: card bianca
                                // e label grigia per restare sobri.
                                const kpis: { label: string; value: string; bar: string; value_cls: string }[] = [
                                    { label: 'Incarichi',       value: String(incarichiValidi.length), bar: 'bg-blue-500',    value_cls: 'text-blue-700' },
                                    { label: 'Relazioni',       value: String(relazioniApprovate),     bar: 'bg-violet-500',  value_cls: 'text-violet-700' },
                                    { label: 'Ore indagini',    value: fmtHM(totOreIndagini),          bar: 'bg-rose-500',    value_cls: 'text-rose-700' },
                                    { label: 'Km percorsi',     value: `${Math.round(totKmApprovati)}`, bar: 'bg-indigo-500', value_cls: 'text-indigo-700' },
                                    { label: 'Turni sicurezza', value: String(turniValidi.length),     bar: 'bg-emerald-500', value_cls: 'text-emerald-700' },
                                    { label: 'Ore turni',       value: fmtHM(totOreTurni),             bar: 'bg-amber-500',   value_cls: 'text-amber-700' },
                                ];
                                return (
                                    <>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                            {kpis.map(k => (
                                                <div
                                                    key={k.label}
                                                    className="relative bg-white border border-gray-200 rounded-lg pl-4 pr-3 py-3 shadow-sm overflow-hidden"
                                                >
                                                    <span className={`absolute left-0 top-0 bottom-0 w-1 ${k.bar}`} />
                                                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{k.label}</div>
                                                    <div className={`mt-1 text-2xl font-bold tabular-nums ${k.value_cls}`}>{k.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-gray-400 -mt-2">
                                            Valori basati sulle approvazioni del titolare/responsabile (esclusi
                                            incarichi/turni rifiutati o revocati e relazioni non ancora approvate).
                                        </p>
                                        {incarichi.length > 0 && (
                                            <div>
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Attività Investigative</h3>
                                                <div className="space-y-2">
                                                    {incarichi.map((inc: any) => {
                                                        // Riepilogo per mandato: ore e km approvati e tipo di auto (aziendale, propria
                                                        // o mista, dato che può cambiare per relazione). Se r.autoAziendale è null si usa inc.autoAziendale.
                                                        let mandOre = 0;
                                                        let mandKm = 0;
                                                        let gAzi = 0;
                                                        let gProp = 0;
                                                        for (const r of (inc.relazioni || [])) {
                                                            if (r.stato !== 'APPROVATO') continue;
                                                            if (r.oreApprovateTotali != null) {
                                                                mandOre += Math.max(0, Number(r.oreApprovateTotali));
                                                            } else {
                                                                const extra = r.oreExtraApprovate != null
                                                                    ? Number(r.oreExtraApprovate)
                                                                    : Number(r.oreExtra || 0);
                                                                mandOre += (Number(r.oreGps) || 0) + Math.max(0, extra);
                                                            }
                                                            mandKm += Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? 0));
                                                            const auto = r.autoAziendale != null ? !!r.autoAziendale : !!inc.autoAziendale;
                                                            if (auto) gAzi++; else gProp++;
                                                        }
                                                        const giorni = gAzi + gProp;
                                                        const autoBadge: { label: string; cls: string } | null = giorni === 0
                                                            ? null
                                                            : gAzi > 0 && gProp === 0
                                                                ? { label: 'Auto aziendale', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                                                                : gProp > 0 && gAzi === 0
                                                                    ? { label: 'Auto privata',   cls: 'bg-slate-50 text-slate-700 border-slate-200' }
                                                                    : { label: `Auto: ${gAzi} aziendale · ${gProp} propria`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
                                                        return (
                                                        <div key={inc.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    {/*
                                                                      Mandato + soggetto, mai il cliente: togliamo l'eventuale " - Nome Cliente"
                                                                      aggiunto dal naming automatico.
                                                                    */}
                                                                    {(() => {
                                                                        // Mandato eliminato: mostriamo "Mandato #ID eliminato"; il lavoro resta
                                                                        // in agenda ma il mandato non è più navigabile.
                                                                        if (inc.praticaDeleted) {
                                                                            const titolo = inc.soggettoNome
                                                                                ? `Mandato #${inc.praticaId} eliminato - ${inc.soggettoNome}`
                                                                                : `Mandato #${inc.praticaId} eliminato`;
                                                                            return <div className="font-medium text-sm text-slate-500 line-through truncate">{titolo}</div>;
                                                                        }
                                                                        const base = (inc.praticaNome || `#${inc.id}`).split(/\s+[-–]\s+/)[0].trim();
                                                                        const titolo = inc.soggettoNome ? `${base} - ${inc.soggettoNome}` : base;
                                                                        return <div className="font-medium text-sm text-gray-900 truncate">{titolo}</div>;
                                                                    })()}
                                                                    {inc.luogo && <div className="text-xs text-gray-500 mt-0.5">{inc.luogo}</div>}
                                                                </div>
                                                                {(() => {
                                                                    // Stato come in calendario e scheda mandato: "Servizio Espletato" col report del giorno.
                                                                    const b = getIncaricoStatoBadge(inc);
                                                                    return <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${b ? b.cls : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{b ? b.label : '-'}</span>;
                                                                })()}
                                                            </div>
                                                            {giorni > 0 && (
                                                                <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                                                                    <span className="px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
                                                                        <span className="opacity-70">Ore appr.</span> <b>{fmtHM(mandOre)}</b>
                                                                    </span>
                                                                    {mandKm > 0 && (
                                                                        <span className="px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                                                                            <span className="opacity-70">Km appr.</span> <b>{Math.round(mandKm)}</b>
                                                                        </span>
                                                                    )}
                                                                    {autoBadge && (
                                                                        <span className={`px-1.5 py-0.5 rounded border ${autoBadge.cls}`}>{autoBadge.label}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {inc.relazioni?.length > 0 && (
                                                                <div className="mt-2 space-y-1">
                                                                    {inc.relazioni.map((r: any) => {
                                                                        // Per giornata solo data, orario e stato: ore e km sono già nei chip del mandato.
                                                                        const data = r.dataRelazione ? new Date(r.dataRelazione).toLocaleDateString('it-IT') : '-';
                                                                        return (
                                                                            <div key={r.id} className="bg-white border border-gray-100 rounded-lg px-2 py-1.5 space-y-1">
                                                                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                                                                    <span className="font-medium text-gray-700 w-20 flex-shrink-0">{data}</span>
                                                                                    {r.orarioInizio && r.orarioFine && <span className="text-gray-500">{r.orarioInizio}–{r.orarioFine}</span>}
                                                                                    <span className={`ml-auto px-1.5 py-0.5 rounded text-xs ${r.stato === 'APPROVATO' ? 'bg-green-100 text-green-700' : r.stato === 'BOZZA' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{r.stato || '-'}</span>
                                                                                    {(r.stato === 'APPROVATO' || r.stato === 'DA_REVISIONARE') && (
                                                                                        <button type="button" onClick={() => setModificaReportId(r.id)}
                                                                                                title="Modifica ore, km e spese approvati"
                                                                                                className="p-1 rounded text-blue-600 hover:bg-blue-50">
                                                                                            <Edit2 size={13}/>
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                                {r.testoRelazione && (
                                                                                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 border-t border-gray-50 pt-1">{r.testoRelazione}</p>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {(!inc.relazioni || inc.relazioni.length === 0) && inc.sessioni?.length > 0 && (
                                                                <div className="mt-1.5 text-xs text-gray-400">{inc.sessioni.length} sessione/i registrata/e</div>
                                                            )}
                                                        </div>
                                                    );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {turni.length > 0 && (
                                            <div>
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Turni di Sicurezza</h3>
                                                <div className="space-y-2">
                                                    {turni.map((t: any) => {
                                                        const b = getTurnoStatoBadge(t);
                                                        const data = t.dataTurno ? new Date(t.dataTurno).toLocaleDateString('it-IT') : '-';
                                                        return (
                                                            <div key={t.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50 flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <div className="font-medium text-sm text-gray-900 truncate">{t.servizio?.nome || '-'}</div>
                                                                    <div className="text-xs text-gray-500 mt-0.5">{data} · {t.oraInizio || '?'}–{t.oraFine || '?'}{t.servizio?.clientName ? ` · ${t.servizio.clientName}` : ''}</div>
                                                                </div>
                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${b ? b.cls : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{b ? b.label : '-'}</span>
                                                                    {t.report?.id && (t.report.stato === 'APPROVATO' || t.report.stato === 'DA_REVISIONARE') && (
                                                                        <button type="button" onClick={() => setModificaReportId(t.report.id)}
                                                                                title="Modifica ore, km e spese approvati"
                                                                                className="p-1 rounded text-blue-600 hover:bg-blue-50">
                                                                            <Edit2 size={13}/>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {incarichi.length === 0 && turni.length === 0 && (
                                            <div className="text-center py-8 text-gray-400">
                                                <p className="text-sm">Nessuna attività trovata nel periodo selezionato.</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0 flex gap-3">
                            <button type="button" onClick={() => setAgendaUser(null)}
                                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm">
                                Chiudi
                            </button>
                            <button onClick={handleDownloadAgenda} disabled={!agendaData}
                                className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors font-medium text-sm flex justify-center items-center gap-2 shadow-sm">
                                <FileDown size={15}/> Scarica PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sopra l'agenda: viene dopo nel DOM. */}
            {modificaReportId !== null && (
                <ModificaReportModal
                    relazioneId={modificaReportId}
                    onClose={() => setModificaReportId(null)}
                    onSaved={() => { setModificaReportId(null); handleLoadAgenda(); }}
                />
            )}

            {/* Modale riepilogo mensile */}
            {riepilogoUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Riepilogo Mensile</h2>
                                <p className="text-xs text-gray-500 mt-0.5">{riepilogoUser.name} {riepilogoUser.surname}</p>
                            </div>
                            <button onClick={() => setRiepilogoUser(null)} className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full shadow-sm"><X size={18}/></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Mese di riferimento</label>
                                <input
                                    type="month"
                                    value={riepilogoMonth}
                                    onChange={e => setRiepilogoMonth(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                                />
                            </div>
                            <p className="text-xs text-gray-400">Genera un PDF con ore, km, veicolo e incarichi svolti. Nessun dato economico incluso.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setRiepilogoUser(null)} className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium text-sm">Annulla</button>
                                <button
                                    onClick={handleDownloadRiepilogo}
                                    disabled={riepilogoLoading}
                                    className="flex-1 px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg font-medium text-sm flex justify-center items-center gap-2 shadow-sm"
                                >
                                    {riepilogoLoading ? <><Loader2 size={15} className="animate-spin"/> Caricamento...</> : <><FileDown size={15}/> Scarica PDF</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Wizard di creazione */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
                            <h2 className="text-lg font-bold text-gray-900">{inviteLink ? "Utente Creato!" : "Aggiungi Nuovo Utente"}</h2>
                            <button onClick={resetCreateModal} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto">
                            {inviteLink ? (
                                <div className="text-center space-y-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600"><Check size={32}/></div>
                                    <p className="text-gray-600">L'utente è stato creato. Copia il link di invito:</p>
                                    <div className="bg-gray-100 p-3 rounded text-sm font-mono break-all text-blue-600 select-all border border-gray-200">{inviteLink}</div>
                                    <button onClick={() => {navigator.clipboard.writeText(inviteLink); addToast("Copiato!", "success")}} className="text-sm font-bold text-blue-600 hover:underline">Copia Link</button>
                                    <button onClick={resetCreateModal} className="w-full bg-gray-900 text-white py-3 rounded-xl mt-4">Chiudi</button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex gap-2 mb-6">
                                        <div className={`h-1 flex-1 rounded-full ${createStep >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                                        <div className={`h-1 flex-1 rounded-full ${createStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                                        <div className={`h-1 flex-1 rounded-full ${createStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                                        <div className={`h-1 flex-1 rounded-full ${createStep >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                                    </div>

                                    {/* Step 1: ruolo */}
                                    {createStep === 1 && (
                                        <div className="space-y-4">
                                            <h3 className="font-bold text-gray-900 text-lg">1. Seleziona Ruolo</h3>
                                            <div className="grid grid-cols-1 gap-3">
                                                <button onClick={() => handleSelectRole(UserRole.COLLABORATORE)} className={`p-4 border rounded-xl flex items-center gap-4 transition-all text-left ${newUserRole === UserRole.COLLABORATORE ? 'border-blue-500 bg-blue-50 shadow-sm' : 'hover:bg-gray-50 hover:border-blue-200'}`}>
                                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><Users size={20}/></div>
                                                    <div><div className="font-bold text-gray-900">Collaboratore</div><div className="text-xs text-gray-500">Operatore sul campo.</div></div>
                                                </button>
                                                <button onClick={() => handleSelectRole(UserRole.RESPONSABILE)} className={`p-4 border rounded-xl flex items-center gap-4 transition-all text-left ${newUserRole === UserRole.RESPONSABILE ? 'border-purple-500 bg-purple-50 shadow-sm' : 'hover:bg-gray-50 hover:border-purple-200'}`}>
                                                    <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><ShieldAlert size={20}/></div>
                                                    <div><div className="font-bold text-gray-900">Responsabile Sede</div><div className="text-xs text-gray-500">Gestisce il planning di una o più sedi.</div></div>
                                                </button>
                                                <button onClick={() => handleSelectRole(UserRole.ADMIN)} className={`p-4 border rounded-xl flex items-center gap-4 transition-all text-left ${newUserRole === UserRole.ADMIN ? 'border-red-500 bg-red-50 shadow-sm' : 'hover:bg-gray-50 hover:border-red-200'}`}>
                                                    <div className="p-2 bg-red-100 text-red-600 rounded-lg"><Shield size={20}/></div>
                                                    <div><div className="font-bold text-gray-900">Titolare</div><div className="text-xs text-gray-500">Accesso globale.</div></div>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2: sedi (selezione multipla) */}
                                    {createStep === 2 && (
                                        <div className="space-y-4 animate-in slide-in-from-right duration-200">
                                            <h3 className="font-bold text-gray-900 text-lg">2. Assegna Sedi</h3>
                                            <p className="text-xs text-gray-500">
                                                {newUserRole === UserRole.RESPONSABILE
                                                    ? "Seleziona le sedi e, per ciascuna, se sarà responsabile o collaboratore. Almeno una sede deve essere gestita."
                                                    : "Seleziona una o più sedi a cui associare il collaboratore (opzionale)."}
                                            </p>
                                            <SediCheckboxList
                                                sedis={sedis}
                                                selected={newUserSedeIds}
                                                onToggle={toggleNewUserSede}
                                                ruoloPerSede={isAdmin && newUserRole === UserRole.RESPONSABILE
                                                    ? { gestite: newUserSediGestite, onChange: setRuoloSede(setNewUserSediGestite) }
                                                    : undefined}
                                            />
                                            <div className="flex gap-3 mt-6">
                                                <button onClick={() => setCreateStep(1)} className="px-4 py-3 text-gray-600 bg-gray-100 rounded-xl font-bold flex-1">Indietro</button>
                                                <button onClick={handleNextStep} className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold flex-1">Continua</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3: dati anagrafici */}
                                    {createStep === 3 && (
                                        <div className="space-y-4 animate-in slide-in-from-right duration-200">
                                            <h3 className="font-bold text-gray-900 text-lg">3. Dati Utente</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div><label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Nome</label><input type="text" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newUserData.name} onChange={e => setNewUserData({...newUserData, name: e.target.value})}/></div>
                                                <div><label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Cognome</label><input type="text" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newUserData.surname} onChange={e => setNewUserData({...newUserData, surname: e.target.value})}/></div>
                                            </div>
                                            <div><label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Email (Login)</label><input type="email" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})}/></div>

                                            <div className="flex gap-3 mt-6">
                                                <button
                                                    onClick={() => {
                                                        // Per il responsabile "Indietro" chiude il wizard. L'ADMIN torna al ruolo,
                                                        // gli altri alla scelta sedi.
                                                        if (isResponsabile) resetCreateModal();
                                                        else setCreateStep(newUserRole === UserRole.ADMIN ? 1 : 2);
                                                    }}
                                                    className="px-4 py-3 text-gray-600 bg-gray-100 rounded-xl font-bold flex-1">
                                                    {isResponsabile ? 'Annulla' : 'Indietro'}
                                                </button>
                                                <button onClick={handleNextStep} className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold flex-1">Avanti (Costi)</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 4: tariffe */}
                                    {createStep === 4 && (
                                        <div className="space-y-4 animate-in slide-in-from-right duration-200">
                                            <div>
                                                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><DollarSign size={20} className="text-green-600"/> 4. Costi del Personale</h3>
                                                <p className="text-sm text-gray-500 mb-4">Imposta quanto l'agenzia paga l'operatore (anche Titolare).</p>
                                            </div>
                                            {renderSelettoreFigura(false)}
                                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                                                <div>
                                                    <label className="text-xs font-bold text-slate-600 uppercase mb-1 block">Tariffa Oraria (Investigazioni)</label>
                                                    <div className="relative">
                                                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" size={16}/>
                                                        <NumberInput
                                                            min={0}
                                                            step="0.5"
                                                            placeholder="0.00"
                                                            className="w-full pl-9 pr-8 p-3 border border-slate-300 rounded-lg outline-none text-right font-mono font-bold"
                                                            value={newUserCosti.costoOrario === 0 && newUserCosti.costoOrario !== undefined ? '' : newUserCosti.costoOrario}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setNewUserCosti({...newUserCosti, costoOrario: val === '' ? 0 : val as any});
                                                            }}
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">€</span>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="text-xs font-bold text-slate-600 uppercase mb-1 block">Tariffa Oraria (Sicurezza)</label>
                                                    <div className="relative">
                                                        <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500" size={16}/>
                                                        <NumberInput
                                                            min={0}
                                                            step="0.5"
                                                            placeholder="0.00"
                                                            className="w-full pl-9 pr-8 p-3 border border-slate-300 rounded-lg outline-none text-right font-mono font-bold"
                                                            value={newUserCosti.costoOrarioSicurezza === 0 && newUserCosti.costoOrarioSicurezza !== undefined ? '' : newUserCosti.costoOrarioSicurezza}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setNewUserCosti({...newUserCosti, costoOrarioSicurezza: val === '' ? 0 : val as any});
                                                            }}
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">€</span>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="text-xs font-bold text-slate-600 uppercase mb-1 block">Rimborso Chilometrico</label>
                                                    <div className="relative">
                                                        <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500" size={16}/>
                                                        <NumberInput
                                                            min={0}
                                                            step="0.01"
                                                            placeholder="0.25"
                                                            className="w-full pl-9 pr-12 p-3 border border-slate-300 rounded-lg outline-none text-right font-mono font-bold"
                                                            value={newUserCosti.rimborsoKm === 0 && newUserCosti.rimborsoKm !== undefined ? '' : newUserCosti.rimborsoKm}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setNewUserCosti({...newUserCosti, rimborsoKm: val === '' ? 0 : val as any});
                                                            }}
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">€/km</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/*
                                              Buono pasto: soglia ore e importo separati per investigazioni e fiduciari
                                              (0 = nessun buono). Il collaboratore non vede mai questi valori.
                                            */}
                                            <BuonoPastoCard
                                                values={newUserCosti}
                                                onChange={(field, v) => setNewUserCosti({...newUserCosti, [field]: v})}
                                            />

                                            <div className="flex gap-3 mt-6">
                                                <button onClick={() => setCreateStep(3)} className="px-4 py-3 text-gray-600 bg-gray-100 rounded-xl font-bold flex-1">Indietro</button>
                                                <button onClick={handleCreateUser} className="px-4 py-3 bg-green-600 text-white rounded-xl font-bold flex-1 flex justify-center items-center gap-2"><Check size={18}/> Salva Utente</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modale modifica */}
            {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
                        <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0">
                            <h2 className="text-lg font-bold text-gray-900">Modifica Utente</h2>
                            <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-1">Permessi e Assegnazioni</h3>
                                {/* Il responsabile non può cambiare ruolo o sede di un utente. */}
                                <div><label className="block text-xs font-bold text-gray-700 mb-1">Ruolo</label><select disabled={isResponsabile} className={`w-full p-2.5 border rounded-lg outline-none ${isResponsabile ? 'bg-gray-100' : 'bg-white'}`} value={editRole} onChange={e => handleEditRoleChange(e.target.value as UserRole)}>{Object.values(UserRole).map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Sedi</label>
                                    {editRole === UserRole.ADMIN ? (
                                        <p className="text-xs text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg p-3">Il Titolare ha accesso globale: nessuna sede da assegnare.</p>
                                    ) : (
                                        <SediCheckboxList
                                            sedis={sedis}
                                            selected={editSedeIds}
                                            onToggle={toggleEditSede}
                                            disabled={isResponsabile}
                                            ruoloPerSede={isAdmin && editRole === UserRole.RESPONSABILE
                                                ? { gestite: editSediGestite, onChange: setRuoloSede(setEditSediGestite) }
                                                : undefined}
                                        />
                                    )}
                                </div>
                                {isAdmin && (editRole === UserRole.ADMIN || editRole === UserRole.RESPONSABILE) && (
                                    <NotificheAreaToggles value={editNotifiche} onChange={setEditNotifiche} />
                                )}
                            </div>

                            <div className="space-y-4 pt-2">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-1 flex items-center gap-2"><DollarSign size={14}/> Costi Personale</h3>
                                {renderSelettoreFigura(true)}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ora (Inv.)</label>
                                        <div className="relative">
                                            <NumberInput
                                                min={0}
                                                step="0.5"
                                                className="w-full p-2 border rounded bg-white text-right font-mono text-sm outline-none"
                                                value={editCosti.costoOrario === 0 ? '' : editCosti.costoOrario}
                                                onChange={e => setEditCosti({...editCosti, costoOrario: e.target.value === '' ? 0 : e.target.value as any})}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">€</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ora (Sic.)</label>
                                        <div className="relative">
                                            <NumberInput
                                                min={0}
                                                step="0.5"
                                                className="w-full p-2 border rounded bg-white text-right font-mono text-sm outline-none"
                                                value={editCosti.costoOrarioSicurezza === 0 ? '' : editCosti.costoOrarioSicurezza}
                                                onChange={e => setEditCosti({...editCosti, costoOrarioSicurezza: e.target.value === '' ? 0 : e.target.value as any})}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">€</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Rimborso KM</label>
                                        <div className="relative">
                                            <NumberInput
                                                min={0}
                                                step="0.01"
                                                className="w-full p-2 border rounded bg-white text-right font-mono text-sm outline-none"
                                                value={editCosti.rimborsoKm === 0 ? '' : editCosti.rimborsoKm}
                                                onChange={e => setEditCosti({...editCosti, rimborsoKm: e.target.value === '' ? 0 : e.target.value as any})}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">€/km</span>
                                        </div>
                                    </div>
                                </div>

                                <BuonoPastoCard
                                    values={editCosti}
                                    onChange={(field, v) => setEditCosti({...editCosti, [field]: v})}
                                />
                            </div>

                            {/*
                              Consensi legali dell'utente (solo ADMIN): documenti accettati con tipo,
                              versione e data, e avviso per gli obbligatori mancanti.
                            */}
                            {isAdmin && modalConsents && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-1 flex items-center gap-2"><ShieldCheck size={14} className="text-blue-600"/> Consensi legali</h3>
                                    {modalConsents.pending.some((d: any) => d.mandatory) ? (
                                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                            <span>
                                                Non ha ancora accettato tutti i documenti obbligatori. Da accettare:{' '}
                                                {modalConsents.pending.filter((d: any) => d.mandatory).map((d: any) => legalDocTypeLabel(d.type)).join(', ')}.
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                            <Check size={14} className="shrink-0" /> Ha accettato tutti i documenti obbligatori.
                                        </div>
                                    )}
                                    {modalConsents.accepted.length > 0 ? (
                                        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                                            {modalConsents.accepted.map((c: any, i: number) => (
                                                <li key={`${c.documentId}-${i}`} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                                                    <span className="text-gray-700 font-medium truncate">{legalDocTypeLabel(c.documentType)} <span className="text-gray-400">v{c.documentVersion}</span></span>
                                                    <span className="text-gray-500 shrink-0">{c.acceptedAt ? new Date(c.acceptedAt).toLocaleString('it-IT') : ''}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-gray-400">Nessun consenso registrato.</p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="p-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
                            <button onClick={() => setEditingUser(null)} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-bold">Annulla</button>
                            <button onClick={handleSaveEdit} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-bold flex items-center gap-2"><Check size={18}/> Salva</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Conferma cambio ruolo / sedi gestite */}
            {confermaRuoli && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setConfermaRuoli(null)}>
                    <div role="dialog" aria-modal="true" className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
                            <ShieldCheck size={20} className="text-purple-600"/>
                            <h3 className="text-lg font-bold text-gray-800">Conferma ruoli per sede</h3>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-gray-700">{confermaRuoli.riepilogo}</p>
                        </div>
                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                            <button onClick={() => setConfermaRuoli(null)} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-bold">Annulla</button>
                            <button
                                onClick={() => { const fn = confermaRuoli.onConfirm; setConfermaRuoli(null); fn(); }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold flex items-center gap-2"
                            >
                                <Check size={16}/> Conferma
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isAgendaSettimanaleOpen && (
                <AgendaSettimanaleModal users={users} onClose={() => setIsAgendaSettimanaleOpen(false)} />
            )}
        </div>
    );
};

// SediCheckboxList: checkbox per associare un utente a più sedi, usata nel
// wizard e nella modale di modifica. `disabled` la rende di sola lettura.
// Con `ruoloPerSede` ogni sede spuntata ha la scelta Responsabile/Collaboratore.
const SediCheckboxList: React.FC<{
    sedis: Sede[];
    selected: number[];
    onToggle: (id: number) => void;
    disabled?: boolean;
    ruoloPerSede?: { gestite: number[]; onChange: (id: number, responsabile: boolean) => void };
}> = ({ sedis, selected, onToggle, disabled, ruoloPerSede }) => (
    <div className="space-y-2 max-h-64 overflow-y-auto">
        {sedis.length === 0 && <p className="text-sm text-gray-400 italic">Nessuna sede disponibile</p>}
        {sedis.map(s => {
            const checked = selected.includes(s.id);
            const gestita = !!ruoloPerSede?.gestite.includes(s.id);
            return (
                <div
                    key={s.id}
                    className={`flex items-center gap-3 p-2.5 border rounded-lg transition-colors ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                    <label className={`flex items-center gap-3 flex-1 min-w-0 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                            type="checkbox"
                            disabled={disabled}
                            checked={checked}
                            onChange={() => onToggle(s.id)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <Building2 size={16} className="text-gray-400 shrink-0"/>
                        <span className="text-sm font-medium text-gray-700 truncate">{s.name}{s.citta ? ` (${s.citta})` : ''}</span>
                    </label>
                    {ruoloPerSede && checked && (
                        <select
                            aria-label={`Ruolo nella sede ${s.name}`}
                            disabled={disabled}
                            value={gestita ? 'RESPONSABILE' : 'COLLABORATORE'}
                            onChange={e => ruoloPerSede.onChange(s.id, e.target.value === 'RESPONSABILE')}
                            className={`shrink-0 text-xs font-bold rounded-lg border px-2 py-1.5 outline-none ${gestita ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-white text-blue-700 border-blue-200'}`}
                        >
                            <option value="RESPONSABILE">Responsabile</option>
                            <option value="COLLABORATORE">Collaboratore</option>
                        </select>
                    )}
                </div>
            );
        })}
    </div>
);

// BuonoPastoCard: soglia ore e importo per investigazioni e servizi fiduciari,
// usata in creazione e modifica. 0 = nessun buono; i campi restano visibili
// così in modifica si può passare da 0 a un valore.
type BuonoPastoFields = {
    sogliaBuonoPastoIncarichiOre: number;
    importoBuonoPastoIncarichi: number;
    sogliaBuonoPastoTurniOre: number;
    importoBuonoPastoTurni: number;
};

const BuonoPastoCard: React.FC<{
    values: BuonoPastoFields;
    onChange: <K extends keyof BuonoPastoFields>(field: K, v: number) => void;
}> = ({ values, onChange }) => {
    const renderPair = (
        labelBox: string,
        sogliaField: 'sogliaBuonoPastoIncarichiOre' | 'sogliaBuonoPastoTurniOre',
        importoField: 'importoBuonoPastoIncarichi' | 'importoBuonoPastoTurni',
    ) => (
        <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{labelBox}</p>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Ore minime</label>
                    <NumberInput
                        min={0} step="0.5"
                        className="w-full p-2 border border-slate-200 rounded bg-white text-right font-mono text-sm outline-none"
                        value={values[sogliaField] === 0 ? '' : values[sogliaField]}
                        onChange={e => onChange(sogliaField, e.target.value === '' ? 0 : Number(e.target.value))}
                    />
                </div>
                <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Importo €</label>
                    <NumberInput
                        min={0} step="0.01"
                        className="w-full p-2 border border-slate-200 rounded bg-white text-right font-mono text-sm outline-none"
                        value={values[importoField] === 0 ? '' : values[importoField]}
                        onChange={e => onChange(importoField, e.target.value === '' ? 0 : Number(e.target.value))}
                    />
                </div>
            </div>
        </div>
    );
    return (
        <div className="space-y-2 pt-2">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-1 flex items-center gap-2">
                <Utensils size={14}/> Buono Pasto
            </h3>
            <p className="text-[11px] text-slate-500">Soglia in ore lavorate + importo per buono. A 0 nessun buono per quella tipologia.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {renderPair('Investigazioni', 'sogliaBuonoPastoIncarichiOre', 'importoBuonoPastoIncarichi')}
                {renderPair('Servizi fiduciari', 'sogliaBuonoPastoTurniOre', 'importoBuonoPastoTurni')}
            </div>
        </div>
    );
};

export default UtentiView;